/**
 * Theme management utility.
 *
 * Supports three modes:
 * - 'light'  — always light
 * - 'dark'   — always dark
 * - 'system' — follows OS / prefers-color-scheme (default)
 *
 * Persists user preference in localStorage.
 * Applies the `dark` class to <html> for Tailwind darkMode: 'class'.
 */

const STORAGE_KEY = 'vestir_appearance';

type ThemeMode = 'light' | 'dark' | 'system';

function getStoredTheme(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed.theme || 'system';
    }
  } catch { /* ignore */ }
  return 'system';
}

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme(mode: ThemeMode) {
  const shouldBeDark =
    mode === 'dark' || (mode === 'system' && systemPrefersDark());

  document.documentElement.classList.toggle('dark', shouldBeDark);
}

/** Call once on app startup to set the initial theme. */
export function initTheme() {
  applyTheme(getStoredTheme());
}

/** Set and persist a new theme mode. */
export function setTheme(mode: ThemeMode) {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, theme: mode }));
  } catch {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ theme: mode }));
  }
  applyTheme(mode);
}

/**
 * Listen for OS theme changes (for 'system' mode).
 * Returns a cleanup function for useEffect.
 */
export function onSystemThemeChange(): () => void {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');

  const handler = () => {
    if (getStoredTheme() === 'system') {
      applyTheme('system');
    }
  };

  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}

export type { ThemeMode };
